import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Package,
  MessageSquare,
  Settings,
  ChevronLeft,
  ChevronRight,
  Database,
  Palette,
} from 'lucide-react';

const Sidebar = ({
  collapsed = false,
  onToggleCollapsed = () => {},
  mobileVisible = true,
  onNavigate = () => {},
}) => {
  const EXPANDED_WIDTH = 280;
  const COLLAPSED_WIDTH = 72;
  const location = useLocation();
  const toggleSidebar = () => onToggleCollapsed?.(!collapsed);

  const navigationItems = [
    {
      path: '/dashboard',
      icon: LayoutDashboard,
      label: 'Dashboard',
      shortLabel: 'Dash',
      description: 'Overview and analytics'
    },
    {
      path: '/coupons',
      icon: Database,
      label: 'Coupons',
      shortLabel: 'Coupons',
      description: 'Coupon rules and execution'
    },
    {
      path: '/ahri',
      icon: Database,
      label: 'AHRI Data',
      shortLabel: 'AHRI',
      description: 'Sync AHRI program data'
    },
    {
      path: '/ahri/assistant',
      icon: MessageSquare,
      label: 'AHRI Assistant',
      shortLabel: 'Assistant',
      description: 'Ask questions on AHRI data'
    },
    {
      path: '/ahri/solr',
      icon: Database,
      label: 'AHRI Search',
      shortLabel: 'Search',
      description: 'SolrCloud search & facets'
    },
    {
      path: '/ai-tryout-studio',
      icon: Palette,
      label: 'Ai Tryout Studio',
      shortLabel: 'Studio',
      description: 'Configure settings, swatches & previews'
    },
    {
      path: '/reqpro/receivers',
      icon: Settings,
      label: 'Ai ReqPro',
      shortLabel: 'ReqPro',
      description: 'EDI receive/post + transformers'
    },
    {
      path: '/documents',
      icon: FileText,
      label: 'Documents',
      shortLabel: 'Docs',
      description: 'Manage and upload documents'
    },
    {
      path: '/v4/documents',
      icon: FileText,
      label: 'Documents v4',
      shortLabel: 'Docs v4',
      description: 'Rich markdown + Pinecone'
    },
    {
      path: '/products',
      icon: Package,
      label: 'Products',
      shortLabel: 'Products',
      description: 'Product management'
    },
    {
      path: '/chat',
      icon: MessageSquare,
      label: 'Chat',
      shortLabel: 'Chat',
      description: 'AI-powered document queries'
    },
    {
      path: '/configurations',
      icon: Settings,
      label: 'Settings',
      shortLabel: 'Config',
      description: 'System configuration'
    }
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item, index) => {
    const Icon = item.icon;
    const isItemActive = isActive(item.path);
    const shortLabel = item.shortLabel || (item.label.length > 4 ? `${item.label.slice(0, 4)}…` : item.label);
    
    return (
      <div key={index} className="nav-item mb-2">
        <Link
          to={item.path}
          title={item.label}
          aria-label={item.label}
          className={`nav-link d-flex align-items-center gap-3 p-3 rounded-3 text-decoration-none ${
            isItemActive ? 'bg-primary text-white' : 'text-dark hover-bg-light'
          } ${collapsed ? 'nav-link-collapsed' : ''}`}
          onClick={() => onNavigate?.()}
        >
          <div className={`icon-wrapper ${collapsed ? 'justify-content-center' : ''}`}>
            <Icon size={20} />
          </div>
          {collapsed ? (
            <div className="collapsed-label text-center mt-2">
              <span className="collapsed-short-label">{shortLabel}</span>
            </div>
          ) : (
            <div className="flex-grow-1">
              <div className="fw-semibold text-truncate">{item.label}</div>
              <div className={`small ${isItemActive ? 'text-white-50' : 'text-muted'}`}>
                {item.description}
              </div>
            </div>
          )}
        </Link>
      </div>
    );
  };

  return (
    <div
      className={`sidebar bg-white border-end ${collapsed ? 'collapsed' : ''} ${
        mobileVisible ? 'show' : ''
      }`}
      style={{
        width: collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`,
        minWidth: collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`,
        maxWidth: collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`,
        flex: `0 0 ${collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH}px`,
        transition: 'width 0.25s ease, min-width 0.25s ease, max-width 0.25s ease',
      }}
    >
      {/* Sidebar header */}
      <div className="sidebar-header p-3 border-bottom">
        <div className="d-flex align-items-center justify-content-between">
          {!collapsed && (
            <h6 className="mb-0 text-primary fw-bold">Navigation</h6>
          )}
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={toggleSidebar}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
      </div>

      {/* Navigation items */}
      <div className="sidebar-content p-3">
        <nav className="nav flex-column">
          {navigationItems.map(renderNavItem)}
        </nav>
      </div>

      {/* Sidebar footer */}
      {!collapsed && (
        <div className="sidebar-footer p-3 border-top mt-auto">
          <div className="text-center">
            <div className="mb-2">
              <img 
                src="/logo.png" 
                alt="AI Tech Assistant Logo" 
                style={{ height: '30px', width: 'auto' }}
                className="d-inline-block"
              />
            </div>
            <div className="small text-muted">v1.0.0</div>
          </div>
        </div>
      )}

      <style jsx>{`
        .sidebar {
          position: sticky;
          top: 0;
          height: calc(100vh - 120px);
          overflow-y: auto;
          z-index: 1000;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          box-shadow: 4px 0 18px rgba(15, 23, 42, 0.08);
          background: #fff;
        }
        
        .sidebar.collapsed {
          min-width: ${COLLAPSED_WIDTH}px;
        }
        
        .sidebar-content {
          flex-grow: 1;
        }
        
        .nav-link {
          transition: all 0.2s ease;
        }
        
        .nav-link:hover {
          transform: translateX(2px);
        }

        .nav-link-collapsed {
          flex-direction: column;
          gap: 0.25rem;
          text-align: center;
          justify-content: center;
          padding: 0.4rem 0.25rem !important;
        }
        
        .nav-link-collapsed .collapsed-label {
          width: 100%;
          display: flex;
          justify-content: center;
        }

        .nav-link-collapsed .icon-wrapper {
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .collapsed-short-label {
          display: inline-block;
          font-size: 0.7rem;
          letter-spacing: 0.01em;
          text-transform: none;
          color: inherit;
          white-space: normal;
          line-height: 1.1;
        }
        
        .hover-bg-light:hover {
          background-color: #f8f9fa !important;
        }
        
        .bg-primary-subtle {
          background-color: rgba(13, 110, 253, 0.1) !important;
        }
        
        @media (max-width: 768px) {
          .sidebar {
            position: fixed;
            left: 0;
            top: 0;
            height: 100vh;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
          }
          
          .sidebar.show {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Sidebar;
